import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SUBJECT_VOCABULARY_BY_VERTICAL,
  hasStatusChoice,
  type SubjectRecord,
  type SubjectStatus,
} from '@ni/shared';
import { useSetSubjectStatus } from '../../api/hooks';
import { statusControlLabel, subjectStatusLabel } from '../../lib/copy';
import { ErrorLine } from '../ui/error-line';
import { StatusDot } from '../ui/status-word';
import './status-control.css';

/**
 * **What this person is to the org, and the one gesture that changes it**
 * (design note §2.4). It sits on the name's line because the status is the
 * second thing the record says, after the name.
 *
 * It is a control, not a badge. The design's "no chips, badges or status cards"
 * line stands for everything else on this head — what is amended is that one
 * word is worth the space, and only because pressing it does something (§1.7).
 *
 * There is no confirmation (§1.3): the club's process is theirs, any word can
 * follow any other, and a wrong click is undone by the next one. Nothing already
 * on record moves with it (§1.4) — the switch decides what the *next* run asks
 * for, and the brief says so on a reading the new status would not revisit.
 *
 * An unknown vertical renders nothing at all: the words belong to a vocabulary
 * this build does not carry, and offering a menu of statuses it cannot name
 * would be worse than the subline standing alone (§0.3).
 *
 * **So does a vertical with one word** (personal-vertical note §7). A menu whose
 * every item is the item already checked is a control that promises a decision
 * and has none to offer, and pressing it can only close it again. The word still
 * applies — the subject has it, the People list prints it, the next run derives
 * from it — it is simply not a thing this office is being asked. A second word
 * added to that vocabulary brings the control back with it and nothing here
 * changes.
 *
 * Split in two the way the intake form is: the route-side half owns the write,
 * the view owns what is on the page, so what is on the page can be asserted
 * without a DOM.
 */
export function StatusControl({
  subjectId,
  subject,
}: {
  subjectId: string;
  subject: SubjectRecord['subject'];
}) {
  const setStatus = useSetSubjectStatus(subjectId);
  const [open, setOpen] = useState(false);

  return (
    <StatusControlView
      vertical={subject.vertical}
      status={subject.status}
      open={open}
      busy={setStatus.isPending}
      /* The old word stays until the server answers with the new one: this
         control reports what the record says, never what was asked for. */
      error={setStatus.isError ? setStatus.error : null}
      onOpen={setOpen}
      onChoose={(next) => {
        setOpen(false);
        // The word they are already on is a dismissal, not a write.
        if (next !== subject.status) setStatus.mutate({ status: next });
      }}
    />
  );
}

export function StatusControlView({
  vertical,
  status,
  open,
  busy,
  error,
  onOpen,
  onChoose,
}: {
  vertical: string;
  status: SubjectStatus;
  open: boolean;
  /**
   * The write is in the air: the control is frozen on the word it still reads,
   * and presses on it do nothing until the server answers.
   *
   * Frozen by refusing the press rather than by `disabled`, which is a keyboard
   * fix and not a styling one. The chosen word's row unmounts the moment the
   * menu closes, so focus has to land back on the trigger — and a `disabled`
   * button cannot hold focus: the browser would drop the reader on `document.body`
   * for the length of the request, and the caret they were navigating with would
   * be gone. `aria-busy` says the same thing to a screen reader that `disabled`
   * said, without taking the element out of the document's focus order.
   */
  busy: boolean;
  error: unknown;
  onOpen: (open: boolean) => void;
  onChoose: (status: SubjectStatus) => void;
}) {
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[vertical];
  const statuses = vocabulary?.statuses ?? [];
  const current = subjectStatusLabel(vertical, status);

  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  /* Which word the keyboard is on. It starts on the one the subject is already
     on — not the top of the list — so the first arrow press moves from where the
     reader is, and every opening puts it back there. */
  const [active, setActive] = useState(() => Math.max(0, statuses.indexOf(status)));

  const close = useCallback(
    (focusTrigger: boolean) => {
      onOpen(false);
      // Escape is the reader backing out and belongs back on the control they
      // opened; an outside click has already chosen somewhere else to be.
      if (focusTrigger) trigger.current?.focus();
    },
    [onOpen],
  );

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      // A dialog's Escape is the dialog's (the same rule the org switcher keeps).
      if (document.querySelector('[aria-modal="true"]')) return;
      if (event.key === 'Escape') close(true);
    };
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) close(false);
    };

    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  /* Focus follows the marked word into the menu, so the first arrow press moves
     from where the reader is rather than from the top of a list. */
  useEffect(() => {
    if (open) items.current[active]?.focus();
  }, [open, active]);

  if (!vocabulary || !current || !hasStatusChoice(vertical)) return null;

  const openAt = (index: number) => {
    setActive(index);
    onOpen(true);
  };

  const move = (delta: number) => {
    setActive((index) => (index + delta + statuses.length) % statuses.length);
  };

  return (
    <div className="status-control" ref={root}>
      <button
        type="button"
        className="status-trigger"
        ref={trigger}
        aria-busy={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={statusControlLabel(current)}
        onClick={() => {
          if (busy) return;
          return open ? close(false) : openAt(Math.max(0, statuses.indexOf(status)));
        }}
        onKeyDown={(event) => {
          if (busy) return;
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            openAt(Math.max(0, statuses.indexOf(status)));
          }
        }}
      >
        <StatusDot vertical={vertical} status={status} />
        {current}
        <span className="chev" aria-hidden="true">
          ▾
        </span>
      </button>

      {open ? (
        /* Real menu roles here, unlike the org switcher's plain buttons: these
           are the values of one setting, one of which is on, and
           `menuitemradio` is the only thing that says so. That promise costs the
           roving focus below — a menu takes Tab away from its items. */
        <div
          className="status-menu"
          role="menu"
          aria-label="Status"
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(1);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(-1);
            } else if (event.key === 'Home') {
              event.preventDefault();
              setActive(0);
            } else if (event.key === 'End') {
              event.preventDefault();
              setActive(statuses.length - 1);
            } else if (event.key === 'Tab') {
              close(false);
            }
          }}
        >
          {statuses.map((word, index) => {
            const label = subjectStatusLabel(vertical, word);
            const marked = word === status;
            return (
              <button
                key={word}
                type="button"
                role="menuitemradio"
                aria-checked={marked}
                className={marked ? 'status-item current' : 'status-item'}
                ref={(node) => {
                  items.current[index] = node;
                }}
                tabIndex={index === active ? 0 : -1}
                disabled={busy}
                onClick={() => {
                  /* Focus first, and back to the control they opened. This row
                     is about to unmount with the menu, and focus on a removed
                     element goes to `document.body` — which puts the reader at
                     the top of the page, with no way back to the word they just
                     chose except by tabbing the whole record again. */
                  trigger.current?.focus();
                  onChoose(word);
                }}
              >
                <span className="mark" aria-hidden="true">
                  {marked ? '✓' : ''}
                </span>
                <StatusDot vertical={vertical} status={word} />
                {label}
              </button>
            );
          })}
        </div>
      ) : null}

      {error ? <ErrorLine error={error} /> : null}
    </div>
  );
}
